/**
 * Single goal entry row — numeric entries and step completions.
 * Used in entries list (detail sheet + card accordion).
 */

import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { Share2, Trash2, Check } from 'lucide-react'
import { motion } from 'framer-motion'
import type { GoalEntry, Goal } from '@turnifly/shared/types'

interface GoalEntryRowProps {
  entry: GoalEntry
  goal: Goal
  isOwner: boolean
  onShare?: (entry: GoalEntry) => void
  onDelete?: (entryId: string) => void
}

export function GoalEntryRow({
  entry,
  goal,
  isOwner,
  onShare,
  onDelete,
}: GoalEntryRowProps): React.ReactElement {
  const { t } = useTranslation()
  const isSteps = goal.goal_type === 'steps'
  const formattedDate = format(new Date(entry.entry_date + 'T00:00:00'), 'dd.MM.yyyy')
  const displayValue  = isSteps ? null : `${entry.value.toLocaleString()} ${goal.unit}`

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      className="flex items-start gap-3 rounded-xl border border-white/[0.06] bg-white/[0.03] px-3 py-2.5"
    >
      {/* Icon */}
      <div className="mt-0.5 w-7 h-7 shrink-0 rounded-lg bg-primary/[0.1] flex items-center justify-center text-sm">
        {isSteps
          ? <Check size={13} className="text-success" />
          : <span>{goal.icon}</span>
        }
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium text-text leading-tight">
          {entry.note || t('goals.entryNoNote')}
        </p>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className="text-[11px] text-text-hint">{formattedDate}</span>
          {displayValue && (
            <>
              <span className="text-white/[0.12] text-[10px]">·</span>
              <span className="text-[11px] font-semibold text-primary/80 tabular-nums">{displayValue}</span>
            </>
          )}
          {entry.is_shared && (
            <>
              <span className="text-white/[0.12] text-[10px]">·</span>
              <span className="text-[10px] text-success/60 font-medium">{t('goals.entryShared')}</span>
            </>
          )}
        </div>
      </div>

      {/* Actions */}
      {isOwner && (
        <div className="flex items-center gap-0.5 shrink-0">
          {onShare && !entry.is_shared && (
            <button
              type="button"
              onClick={() => onShare(entry)}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-text-hint/50 hover:text-primary hover:bg-primary/[0.08] transition-colors tap-highlight-transparent"
            >
              <Share2 size={13} />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(entry.id)}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-text-hint/50 hover:text-danger hover:bg-danger/[0.08] transition-colors tap-highlight-transparent"
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>
      )}
    </motion.div>
  )
}
